import React, { useState, useRef, useEffect } from "react";
import "./BrandscapeHub.scss";

/**
 * BRANDSCAPE HUB - Where gaming meets brand immersion
 * Revenue-first design that doesn't feel like a dashboard
 */
const BrandscapeHub: React.FC = () => {
    const [activeBrand, setActiveBrand] = useState("snapple");
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    // Parallax mouse tracking
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setMousePos({
                x: (e.clientX - rect.left) / rect.width,
                y: (e.clientY - rect.top) / rect.height,
            });
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    const brands = {
        snapple: {
            name: "Snapple",
            color: "#FF6B35",
            bg: "/images/brands/snapple-beach.jpg",
            logo: "/images/brands/snapple-logo.png",
            tagline: "REAL FACT #1337: GAMERS LOVE SNAPPLE",
            prize: "Gaming Chair + Year Supply",
            deadline: "48H LEFT",
        },
        cod: {
            name: "Call of Duty",
            color: "#00D4FF",
            bg: "/images/brands/cod-warzone.jpg",
            logo: "/images/brands/cod-logo.png",
            tagline: "WARZONE ELITE CHALLENGE",
            prize: "Custom PC Build",
            deadline: "5 DAYS",
        },
        redbull: {
            name: "Red Bull",
            color: "#DB0A40",
            bg: "/images/brands/redbull-esports.jpg",
            logo: "/images/brands/redbull-logo.png",
            tagline: "CLUTCH OR KICK TOURNAMENT",
            prize: "$10,000 Prize Pool",
            deadline: "LIVE NOW",
        },
    };

    const currentBrand = brands[activeBrand];

    return (
        <div className="brandscape-hub" ref={containerRef}>
            {/* DYNAMIC BRAND BACKGROUND */}
            <div className="brand-atmosphere">
                <div
                    className="brand-image-layer"
                    style={{
                        backgroundImage: `url(${currentBrand.bg})`,
                        transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px) scale(1.1)`,
                    }}
                />
                <div className="brand-gradient-overlay" style={{
                    background: `radial-gradient(circle at ${mousePos.x * 100}% ${mousePos.y * 100}%, transparent 30%, rgba(0,0,0,0.8) 70%)`,
                }} />
                <div className="brand-color-wash" style={{
                    background: `linear-gradient(45deg, ${currentBrand.color}22 0%, transparent 50%)`,
                }} />
            </div>

            {/* FLOATING BRAND SELECTOR */}
            <div className="brand-orbit">
                {Object.entries(brands).map(([key, brand], index) =>
                    <div
                        key={key}
                        className={`orbit-brand ${activeBrand === key ? "active" : ""}`}
                        style={{
                            "--angle": `${index * 120}deg`,
                            "--color": brand.color,
                        } as any}
                        onClick={() => setActiveBrand(key)}
                    >
                        <img src={brand.logo} alt={brand.name} />
                        <div className="orbit-glow" />
                    </div>)}
            </div>

            {/* HERO BRAND CAMPAIGN */}
            <div className="campaign-hero">
                <div className="hero-content">
                    <img src={currentBrand.logo} alt={currentBrand.name} className="hero-logo" />
                    <h1 className="hero-tagline">{currentBrand.tagline}</h1>
                    <div className="hero-prize">
                        <span className="prize-label">GRAND PRIZE</span>
                        <span className="prize-value">{currentBrand.prize}</span>
                    </div>
                    <div className="hero-timer" style={{ color: currentBrand.color }}>
                        <span className="timer-icon">⏱</span>
                        <span className="timer-value">{currentBrand.deadline}</span>
                    </div>
                </div>

                {/* 3D QUEST CARDS FLOATING IN SPACE */}
                <div className="quest-constellation">
                    <div
                        className="quest-card floating"
                        style={{
                            "--float-x": `${mousePos.x * 50 - 25}px`,
                            "--float-y": `${mousePos.y * 30 - 15}px`,
                            "--delay": "0s",
                        } as any}
                        onMouseEnter={() => setHoveredCard("quest1")}
                        onMouseLeave={() => setHoveredCard(null)}
                    >
                        <div className="card-face">
                            <div className="card-type">ELIMINATION</div>
                            <h3 className="card-title">SNIPER ELITE</h3>
                            <div className="card-progress">
                                <div className="progress-orbs">
                                    {[...Array(5)].map((_, i) =>
                                        <div key={i} className={`orb ${i < 3 ? "filled" : ""}`} />)}
                                </div>
                            </div>
                            <div className="card-reward">
                                <span className="reward-xp">500 XP</span>
                                <span className="reward-fuel">+💎</span>
                            </div>
                        </div>
                        <div className="card-depth" />
                        {hoveredCard === "quest1" && <div className="card-spotlight" />}
                    </div>

                    <div
                        className="quest-card floating"
                        style={{
                            "--float-x": `${mousePos.x * -40 + 20}px`,
                            "--float-y": `${mousePos.y * -50 + 25}px`,
                            "--delay": "0.5s",
                        } as any}
                        onMouseEnter={() => setHoveredCard("quest2")}
                        onMouseLeave={() => setHoveredCard(null)}
                    >
                        <div className="card-face brand-special" style={{ borderColor: currentBrand.color }}>
                            <img src={currentBrand.logo} alt={currentBrand.name} className="card-brand" />
                            <h3 className="card-title">BRAND EXCLUSIVE</h3>
                            <p className="card-desc">Complete 3 matches drinking {currentBrand.name}</p>
                            <div className="card-reward premium">
                                <span className="reward-value">LOOTBOX</span>
                            </div>
                        </div>
                        <div className="card-depth" style={{ background: currentBrand.color }} />
                        {hoveredCard === "quest2" && <div className="card-spotlight" />}
                    </div>

                    <div
                        className="quest-card floating"
                        style={{
                            "--float-x": `${mousePos.x * 60 - 30}px`,
                            "--float-y": `${mousePos.y * -40 + 20}px`,
                            "--delay": "1s",
                        } as any}
                        onMouseEnter={() => setHoveredCard("quest3")}
                        onMouseLeave={() => setHoveredCard(null)}
                    >
                        <div className="card-face urgent">
                            <div className="card-timer">23:47</div>
                            <h3 className="card-title">FLASH DROP</h3>
                            <div className="card-icon">⚡</div>
                            <div className="card-reward">
                                <span className="reward-value">1000 FUEL</span>
                            </div>
                        </div>
                        <div className="card-depth pulse" />
                        {hoveredCard === "quest3" && <div className="card-spotlight" />}
                    </div>
                </div>
            </div>

            {/* PLAYER HUD OVERLAY */}
            <div className="player-hud">
                <div className="hud-element hud-profile">
                    <div className="profile-avatar">
                        <img src="/placeholder-avatar.jpg" alt="Player" />
                        <div className="level-badge">42</div>
                    </div>
                    <div className="profile-stats">
                        <div className="stat-name">DEATHKILLER99</div>
                        <div className="xp-mini">
                            <div className="xp-fill" style={{ width: "67%" }} />
                            <span className="xp-text">67%</span>
                        </div>
                    </div>
                </div>

                <div className="hud-element hud-currency">
                    <div className="currency-item">
                        <span className="currency-icon">⚡</span>
                        <span className="currency-value">2,847</span>
                        <span className="currency-label">FUEL</span>
                    </div>
                    <div className="currency-item premium">
                        <span className="currency-icon">💎</span>
                        <span className="currency-value">12</span>
                        <span className="currency-label">GEMS</span>
                    </div>
                </div>

                <div className="hud-element hud-guild">
                    <div className="guild-banner">
                        <span className="guild-icon">⚔️</span>
                        <span className="guild-name">APEX PREDATORS</span>
                        <span className="guild-rank">#7</span>
                    </div>
                </div>
            </div>

            {/* LIVE FEED TICKER */}
            <div className="live-ticker">
                <div className="ticker-content">
                    <span className="ticker-item">
                        <span className="ticker-icon">🏆</span>
                        <span className="ticker-text">xKiller99 won Snapple Gaming Chair!</span>
                    </span>
                    <span className="ticker-item">
                        <span className="ticker-icon">💎</span>
                        <span className="ticker-text">Phoenix opened LEGENDARY lootbox</span>
                    </span>
                    <span className="ticker-item">
                        <span className="ticker-icon">🔥</span>
                        <span className="ticker-text">Guild War: 2H remaining</span>
                    </span>
                    <span className="ticker-item">
                        <span className="ticker-icon">⚡</span>
                        <span className="ticker-text">FLASH QUEST: 500 Fuel in 30min</span>
                    </span>
                </div>
            </div>

            {/* BOTTOM ACTION DOCK */}
            <div className="action-dock">
                <button className="dock-button primary">
                    <span className="button-bg" />
                    <span className="button-content">
                        <span className="button-icon">🎮</span>
                        <span className="button-text">PLAY NOW</span>
                    </span>
                </button>

                <div className="dock-section">
                    <button className="dock-button">
                        <span className="button-icon">📦</span>
                        <span className="button-badge">3</span>
                    </button>
                    <button className="dock-button">
                        <span className="button-icon">🏪</span>
                    </button>
                    <button className="dock-button">
                        <span className="button-icon">👥</span>
                    </button>
                </div>

                <div className="journey-progress">
                    <div className="journey-label">JOURNEY: SNIPER MASTER</div>
                    <div className="journey-bar">
                        <div className="journey-fill" style={{ width: "45%" }}>
                            <div className="journey-glow" />
                        </div>
                        <div className="journey-checkpoints">
                            <div className="checkpoint done" />
                            <div className="checkpoint done" />
                            <div className="checkpoint active" />
                            <div className="checkpoint" />
                            <div className="checkpoint" />
                        </div>
                    </div>
                    <div className="journey-reward">🎁 MYTHIC SKIN</div>
                </div>
            </div>

            {/* IMMERSIVE EFFECTS */}
            <div className="immersion-layer">
                <div className="light-leak" style={{
                    background: `radial-gradient(circle at ${mousePos.x * 100}% 0%, ${currentBrand.color}44 0%, transparent 50%)`,
                }} />
                <div className="scan-effect" />
                <div className="particle-field">
                    {[...Array(20)].map((_, i) =>
                        <div
                            key={i}
                            className="particle"
                            style={{
                                "--delay": `${Math.random() * 5}s`,
                                "--duration": `${5 + Math.random() * 5}s`,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                            } as any}
                        />)}
                </div>
            </div>
        </div>
    );
};

export default BrandscapeHub;